import { useEffect } from 'react';

/**
 * [모듈 1] 레벨 로드맵 자동 회전 스토리 미리보기.
 * rootRef 안에서 레벨 칩([data-level-step])과 미리보기 카드([data-level-track],
 * [data-level-kicker], [data-level-title], [data-level-text], [data-level-image])를 찾아
 * hover/focus/click 시 오른쪽 카드가 해당 레벨로 바뀐다. 방치하면 4초마다 자동으로 다음
 * 레벨로 회전하고, 마우스를 올리거나 포커스하면 회전을 멈춘다(isPaused).
 *
 * @param {import('react').RefObject<HTMLElement>} rootRef - 레벨 트랙 + 스토리 카드를 함께 감싸는 컨테이너
 */
export default function useLevelStoryPreview(rootRef) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const steps = Array.from(root.querySelectorAll('[data-level-step]'));
    const track = root.querySelector('[data-level-track]');
    const kicker = root.querySelector('[data-level-kicker]');
    const title = root.querySelector('[data-level-title]');
    const text = root.querySelector('[data-level-text]');
    const image = root.querySelector('[data-level-image]');
    if (!steps.length || !kicker || !title || !text || !image) return undefined;

    let activeIndex = Math.max(
      0,
      steps.findIndex((step) => step.classList.contains('is-active'))
    );
    let slideTimer = null;
    let switchTimer = null;
    let isPaused = false;

    steps.forEach((step) => {
      if (!step.dataset.image) return;
      const preload = new Image();
      preload.src = step.dataset.image;
    });

    const centerStepInTrack = (step) => {
      if (!track || track.scrollWidth <= track.clientWidth) return;
      const targetLeft = step.offsetLeft - (track.clientWidth - step.offsetWidth) / 2;
      track.scrollTo({ left: targetLeft, behavior: 'smooth' });
    };

    const setActiveLevel = (step, options = {}) => {
      const level = step.dataset.level || '';
      const levelTitle = step.dataset.title || '';
      const copy = step.dataset.copy || '';
      const imageSrc = step.dataset.image || '';
      activeIndex = steps.indexOf(step);

      steps.forEach((item) => {
        const isActive = item === step;
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-pressed', String(isActive));
      });

      kicker.textContent = `${level} ${levelTitle}`.trim();
      title.textContent = `${levelTitle} 스토리`;
      text.textContent = copy;

      if (imageSrc && image.getAttribute('src') !== imageSrc) {
        image.classList.add('is-switching');
        clearTimeout(switchTimer);
        switchTimer = window.setTimeout(() => {
          image.src = imageSrc;
          image.alt = `${level} ${levelTitle} 스토리 이미지`;
          image.classList.remove('is-switching');
        }, 90);
      }

      if (options.scroll) {
        centerStepInTrack(step);
      }
    };

    const startAutoSlide = () => {
      window.clearInterval(slideTimer);
      slideTimer = window.setInterval(() => {
        if (isPaused || document.hidden) return;
        const nextIndex = (activeIndex + 1) % steps.length;
        setActiveLevel(steps[nextIndex], { scroll: true });
      }, 4000);
    };

    const cleanups = [];
    steps.forEach((step) => {
      step.setAttribute('aria-pressed', step.classList.contains('is-active') ? 'true' : 'false');
      const onEnter = () => {
        isPaused = true;
        setActiveLevel(step);
      };
      const onFocus = () => {
        isPaused = true;
        setActiveLevel(step);
      };
      const onBlur = () => {
        isPaused = false;
      };
      const onClick = () => {
        setActiveLevel(step, { scroll: true });
      };
      const onKeydown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          setActiveLevel(step, { scroll: true });
        }
      };
      step.addEventListener('mouseenter', onEnter);
      step.addEventListener('focus', onFocus);
      step.addEventListener('blur', onBlur);
      step.addEventListener('click', onClick);
      step.addEventListener('keydown', onKeydown);
      cleanups.push(() => {
        step.removeEventListener('mouseenter', onEnter);
        step.removeEventListener('focus', onFocus);
        step.removeEventListener('blur', onBlur);
        step.removeEventListener('click', onClick);
        step.removeEventListener('keydown', onKeydown);
      });
    });

    const onTrackMouseLeave = () => {
      isPaused = false;
    };
    if (track) track.addEventListener('mouseleave', onTrackMouseLeave);

    startAutoSlide();

    return () => {
      window.clearInterval(slideTimer);
      clearTimeout(switchTimer);
      cleanups.forEach((fn) => fn());
      if (track) track.removeEventListener('mouseleave', onTrackMouseLeave);
    };
  }, [rootRef]);
}
